import { createContext, use } from "react";

import type { ResultRow } from "./parserConfidence";

// Shared state bridge for the dedicated row-review surface (7HAI-349).
//
// The per-row part-type correction used to live inline inside
// ParserConfidencePanel, behind the Adjust toggle (7HAI-327/7HAI-345). Testing
// (24.06.2026, Jari) found that crammed inline list terrible/cluttered, so the
// row correction moves OUT of the result card onto its own dedicated page
// (RowReviewPage, route /admin_dashboard/calculate/row-review).
//
// The parse result (rows) lives in the calc flow (CalculateFileCard's local
// state). The row-review page is a sibling route — it has no access to that
// state — so this lightweight context, mounted by RowReviewProvider around
// BOTH the card route and the row-review route, carries the data across:
//   - the card PUBLISHES the active sheet's classified rows + the file name +
//     a submit callback whenever it has a parse result;
//   - the row-review page CONSUMES them, renders the pickers, and calls submit.
//
// When the page is opened without a published payload (deep link / refresh,
// where the card's in-memory state is gone) `rows` is empty and the page shows
// a "run a calculation first" empty state. Nothing here is persisted.
//
// Context + hook + types live in this `.ts` module (not the provider `.tsx`)
// so the provider file only exports a component — keeps react-refresh happy.

export interface RowReviewPayload {
  // The active sheet's classified rows (row_index / predicted / description),
  // from the /api/parse classify_rows pre-flight.
  rows: ResultRow[];
  // Source file name, sent with the correction batch for per-company learning.
  fileName: string | null;
  // Submit a batch of row corrections (row_index -> chosen part-type). Resolves
  // with the BE's learned count, or null when the endpoint is absent (older
  // BE) — the page degrades quietly in that case. Owned by the card so the
  // exact submit plumbing (useParserCorrection) stays in one place.
  submit: (rowCorrections: Record<number, string>) => Promise<number | null>;
}

export interface RowReviewContextValue {
  payload: RowReviewPayload | null;
  setPayload: (payload: RowReviewPayload | null) => void;
}

export const RowReviewContext = createContext<RowReviewContextValue | undefined>(undefined);

// Read the row-review bridge. Returns a no-op fallback when used outside a
// provider so a stray render (e.g. the card mounted somewhere without the
// provider) never throws — it simply publishes into the void.
export const useRowReview = (): RowReviewContextValue => {
  const ctx = use(RowReviewContext);
  if (!ctx) {
    return { payload: null, setPayload: () => {} };
  }
  return ctx;
};
